import "./FormManagement.scss";
import {
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { Add, ArrowBack } from "@mui/icons-material";
import { Button, Grid, Typography } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import {
  SurveyConfiguration,
  getOrgSurveyConfigPortal,
  getOrgSurveyVersionOverview,
} from "@rubi/api-generated";
import { useNavigate, useParams, useLocation } from "react-router";

import { LinkCell } from "@/common/DataGridCells";

import { useFormBuilderContext } from "./FormContextProvider";

const useDynamicColumnWidths = (
  topPageRef: MutableRefObject<HTMLDivElement | null>,
) => {
  const calculateColumnWidths = useCallback((parentWidth?: number) => {
    const baseWidth = parentWidth
      ? parentWidth * 0.85
      : window.innerWidth * 0.85;

    const ratios = {
      icon: 0.5,
      name: 3,
      survey_class: 2,
      sort_order: 0.6,
      is_supplemental: 1,
      hidden: 1,
    };
    const totalRatios = Object.values(ratios).reduce(
      (acc, val) => acc + val,
      0,
    );

    const columnWidths = {};
    for (const [key, value] of Object.entries(ratios)) {
      columnWidths[key] = (baseWidth / totalRatios) * value;
    }

    return columnWidths;
  }, []);

  const [columnWidths, setColumnWidths] = useState<{
    [key: string]: number;
  }>(() =>
    calculateColumnWidths(topPageRef.current?.parentElement?.offsetWidth),
  );

  useEffect(() => {
    const handleResize = () => {
      const parentWidth = topPageRef.current?.parentElement?.offsetWidth;
      setColumnWidths(calculateColumnWidths(parentWidth));
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [topPageRef, calculateColumnWidths]);

  return columnWidths;
};

const FormListView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const orgId = params.orgId;

  const app = location.pathname.includes("rubiintel")
    ? "rubiintel"
    : "rubireportv2";

  const { surveyConfigs, setSurveyConfigs, setSurveysWithActiveVersions } =
    useFormBuilderContext();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [configsResponse, surveysResponse] = await Promise.all([
          getOrgSurveyConfigPortal({ path: { org_id: orgId ?? "" } }),
          getOrgSurveyVersionOverview({
            path: { org_id: orgId ?? "" },
            query: { status: "active" },
          }),
        ]);
        setSurveyConfigs(configsResponse?.data!.surveys || []);
        setSurveysWithActiveVersions(surveysResponse?.data!.surveys || []);
      } catch (error) {
        navigate("/");
      }
    };

    fetchData();
  }, [orgId, navigate]);

  const topPageRef = useRef<HTMLDivElement | null>(null);
  const columnWidths = useDynamicColumnWidths(topPageRef);

  const renderLinkCell = (value: string, path: string) => (
    <LinkCell
      value={value}
      onClick={() =>
        navigate(path, {
          replace: false,
          state: { orgId },
        })
      }
    />
  );

  const createNewFormHandler = () => {
    navigate(`./new`, {
      replace: false,
      state: { orgId },
    });
  };

  const columns: GridColDef<SurveyConfiguration>[] = [
    {
      field: "icon",
      headerName: "",
      width: columnWidths.icon,
      renderCell: ({ row }) => {
        const iconUrl = row.description?.image_url;
        return (
          <div className="icon-cell">
            {iconUrl ? <img src={iconUrl} alt="" /> : "❔"}
          </div>
        );
      },
      sortable: false,
    },
    {
      field: "name",
      headerName: "Name",
      width: columnWidths.name,
      renderCell: ({ value, row }) =>
        renderLinkCell(value, `./${row.survey_class}`),
    },
    {
      field: "survey_class",
      headerName: "Survey Class",
      width: columnWidths.survey_class,
    },
    {
      field: "sort_order",
      headerName: "Order",
      width: columnWidths.sort_order,
      type: "number",
    },
    {
      field: "is_supplemental",
      headerName: "Supplemental",
      width: columnWidths.is_supplemental,
      type: "boolean",
    },
    {
      field: "hidden",
      headerName: "Hidden",
      width: columnWidths.hidden,
      type: "boolean",
    },
  ];

  const handleBack = () => {
    navigate(`/${app}/${orgId}/forms`);
  };

  return (
    <div className="form_list_container">
      <div id="top-page-div" ref={topPageRef}></div>
      <Button onClick={handleBack} startIcon={<ArrowBack />} sx={{ mb: 2 }}>
        Back to Form Management
      </Button>
      <Grid container justifyContent="space-between" alignItems="center">
        <Grid size={{ xs: 12, sm: 6 }}>
          <Typography variant="h4" gutterBottom>
            Forms Management
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }} style={{ textAlign: "right" }}>
          <Button
            variant="contained"
            color="primary"
            onClick={createNewFormHandler}
            startIcon={<Add />}
            className="create-form-button"
          >
            Create Form
          </Button>
        </Grid>
      </Grid>

      <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
        Survey Configurations
      </Typography>
      <DataGrid
        rows={surveyConfigs}
        sx={{
          "& .MuiDataGrid-row:not(.MuiDataGrid-row--dynamicHeight)>.MuiDataGrid-cell":
            {
              whiteSpace: "normal",
              wordWrap: "break-word",
              lineHeight: "inherit",
              display: "flex",
              alignItems: "center",
            },
        }}
        columns={columns}
        rowSelection={false}
        getRowId={(row) => row.id}
        className="form-list-datagrid"
      />
    </div>
  );
};

export default FormListView;
